import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Typography,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip
} from '@mui/material';
import {
  MoreVert,
  Message,
  Person,
  Remove,
  TrendingUp
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';

const FriendsList = ({ friends, onRemove, onMessage, onViewProfile, loading }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const handleMenuClick = (event, friend) => {
    setAnchorEl(event.currentTarget);
    setSelectedFriend(friend);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFriend(null);
  };

  const handleRemoveFriend = () => {
    setConfirmRemove(selectedFriend);
    handleMenuClose();
  };

  const confirmRemoveFriend = async () => {
    if (confirmRemove && onRemove) {
      await onRemove(confirmRemove.user_id || confirmRemove.id);
      setConfirmRemove(null);
    }
  };

  const formatLastActivity = (lastActivity) => {
    if (!lastActivity) return 'No recent activity';
    
    try {
      const date = parseISO(lastActivity);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Unknown activity';
    }
  };

  const getActivityStatus = (lastActivity) => {
    if (!lastActivity) return 'inactive';
    
    try {
      const date = parseISO(lastActivity);
      const now = new Date();
      const diffHours = (now - date) / (1000 * 60 * 60);
      
      if (diffHours < 24) return 'active';
      if (diffHours < 72) return 'recent';
      return 'inactive';
    } catch (error) {
      return 'inactive';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'recent': return 'warning';
      default: return 'default';
    }
  };

  if (!friends || friends.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No friends yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Start by searching for people to connect with!
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <List disablePadding>
        {friends.map((friend) => {
          const activityStatus = getActivityStatus(friend.lastActivity);
          
          return (
            <ListItem key={friend.user_id || friend.id} divider>
              <ListItemAvatar>
                <Avatar 
                  src={friend.avatar || friend.profilePicture} 
                  sx={{ bgcolor: 'primary.main' }}
                >
                  {(friend.display_name || friend.name || friend.username || 'U').charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1" fontWeight="medium">
                      {friend.display_name || friend.name || friend.username}
                    </Typography>
                    <Chip 
                      label={activityStatus} 
                      size="small" 
                      color={getStatusColor(activityStatus)}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatLastActivity(friend.lastActivity)}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.5}>
                      {friend.currentStreak && friend.currentStreak > 0 && (
                        <Chip 
                          label={`${friend.currentStreak} day streak`} 
                          size="small" 
                          color="success"
                          variant="filled"
                          icon={<TrendingUp />}
                        />
                      )}
                      {friend.connected_at && (
                        <Typography variant="caption" color="text.secondary">
                          Friends since {formatDistanceToNow(parseISO(friend.connected_at))} ago
                        </Typography>
                      )}
                    </Box>
                  </Box>
                }
              />
              
              <ListItemSecondaryAction>
                <Box display="flex" gap={0.5}>
                  {onMessage && (
                    <Tooltip title="Send message">
                      <IconButton 
                        size="small" 
                        onClick={() => onMessage(friend.user_id || friend.id)}
                      >
                        <Message />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {onViewProfile && (
                    <Tooltip title="View profile">
                      <IconButton 
                        size="small" 
                        onClick={() => onViewProfile(friend.user_id || friend.id)}
                      >
                        <Person />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleMenuClick(e, friend)}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {onViewProfile && (
          <MenuItem onClick={() => {
            onViewProfile(selectedFriend?.user_id || selectedFriend?.id);
            handleMenuClose();
          }}>
            <Person sx={{ mr: 1 }} /> View Profile
          </MenuItem>
        )}
        
        {onMessage && (
          <MenuItem onClick={() => {
            onMessage(selectedFriend?.user_id || selectedFriend?.id);
            handleMenuClose();
          }}>
            <Message sx={{ mr: 1 }} /> Send Message
          </MenuItem>
        )}
        
        <MenuItem onClick={handleRemoveFriend} sx={{ color: 'error.main' }}>
          <Remove sx={{ mr: 1 }} /> Remove Friend
        </MenuItem>
      </Menu>

      {/* Remove Confirmation Dialog */}
      <Dialog open={Boolean(confirmRemove)} onClose={() => setConfirmRemove(null)}>
        <DialogTitle>Remove Friend</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{confirmRemove?.display_name || confirmRemove?.name}</strong> from your friends list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button onClick={confirmRemoveFriend} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FriendsList;